import { z } from 'zod';

/**
 * Stable error codes shared by HTTP responses and WebSocket messages.
 * Codes are part of the public contract: never rename or reuse one. Clients translate them
 * through the i18n catalog (`errors.<code>`); the `message` field is for developers only.
 */
export const ErrorCode = {
  // Generic
  ValidationFailed: 'validation_failed',
  NotFound: 'not_found',
  Conflict: 'conflict',
  RateLimited: 'rate_limited',
  InternalError: 'internal_error',
  ServiceUnavailable: 'service_unavailable',

  // Sessions and permissions
  Unauthorized: 'unauthorized',
  SessionExpired: 'session_expired',
  SessionReplaced: 'session_replaced',
  PermissionDenied: 'permission_denied',
  DevToolsDisabled: 'dev_tools_disabled',
  SignInFailed: 'sign_in_failed',

  // Accounts and characters (ADR 0029)
  CharacterRequired: 'character_required',
  CharacterLimitReached: 'character_limit_reached',
  CharacterNameTaken: 'character_name_taken',
  /** A character its owner is deleting (CHAR-DELETE, CD-1): it cannot be played until cancelled. */
  CharacterDeleting: 'character_deleting',

  // Realtime protocol
  ProtocolMismatch: 'protocol_mismatch',
  MalformedMessage: 'malformed_message',

  // Game rules (PrimeraVersion.md §12 > Contratos)
  InsufficientResources: 'insufficient_resources',
  StorageFull: 'storage_full',
  BackpackFull: 'backpack_full',
  OutOfRange: 'out_of_range',
  LocationOccupied: 'location_occupied',
  OfferClosed: 'offer_closed',
  InsufficientFunds: 'insufficient_funds',
  OfferLimitReached: 'offer_limit_reached',
  CooldownActive: 'cooldown_active',
  NodeUnavailable: 'node_unavailable',
  ActionInterrupted: 'action_interrupted',
  ProtectedZone: 'protected_zone',
  InCombat: 'in_combat',
  CharacterDead: 'character_dead',
  FullHealth: 'full_health',
  CampOwned: 'camp_owned',
  CampDefended: 'camp_defended',
  CampProtected: 'camp_protected',
  CampEmpty: 'camp_empty',
  CampNotDamaged: 'camp_not_damaged',
  InsufficientPoints: 'insufficient_points',
  RequirementNotMet: 'requirement_not_met',
  BodyFull: 'body_full',
  ToleranceExceeded: 'tolerance_exceeded',
  NotEnoughFocus: 'not_enough_focus',
  NoDodgeCharges: 'no_dodge_charges',
  Staggered: 'staggered',
  DuelBusy: 'duel_busy',
  NoStamina: 'no_stamina',
  /** A party holds five (SOC-PARTY, GR-1). */
  PartyFull: 'party_full',
  /** One of the two is already in another party. */
  PartyBusy: 'party_busy',
  /** Another company bears that name (B105). */
  CompanyNameTaken: 'company_name_taken',
  /** A company holds `companies.maxMembers`. */
  CompanyFull: 'company_full',
  /** The character (or the one invited) already belongs to a company. */
  CompanyBusy: 'company_busy',
  /** Withdrawing from the company's treasury needs seniority (GR-4). */
  CompanyTooRecent: 'company_too_recent',
  /** The weapon shoots ammunition and the quiver has none (ITEM-AMMO AM-1). */
  NoAmmo: 'no_ammo',
  /** Fishing needs a rod worn in the main hand (RES-FISH). */
  NoRod: 'no_rod',
  /** The line was cast at no water (RES-FISH). */
  NotWater: 'not_water',
  /** The point is too far, on another layer or behind something (RES-FISH). */
  OutOfReach: 'out_of_reach',
  /** The node can only be gathered with a tool of its kind in the backpack (R-28). */
  ToolRequired: 'tool_required',
  /** The tools of the node's kind carried are below its tier (RES-GATHER RG-2): `details.tier`. */
  ToolTooWeak: 'tool_too_weak',
  /** The node asks a rank of its mastery the character lacks (RG-5): `details.mastery`, `.rank`. */
  RankTooLow: 'rank_too_low',
  /** The lot breaks one of its rules (HOME-CLAIM, `lotRefusal`): `details.reason` says which. */
  LotRefused: 'lot_refused',
  /** The project's stage lacks its materials (BUILD, plan E2): `details.lacking` says what. */
  StageUnsupplied: 'stage_unsupplied',
  /** The room's rent ran out (HOME, plan C2): nothing comes out until it is paid again. */
  RoomExpired: 'room_expired',
  /** Someone else's storage is locked (plan C6): force its lock first. */
  Locked: 'locked',
  /** The lock is already open, or there is none to force (plan C6). */
  AlreadyOpen: 'already_open',
  /** A small site's custodians still hold it (plan C10): defeat them first. */
  SiteDefended: 'site_defended',
  /** Someone holds that small site already (plan C10). */
  SiteHeld: 'site_held',
  /** What is built cannot be damaged now: outside its damage window (plan H1). */
  NoDamageWindow: 'no_damage_window',
  /** A fallen small site's custody has not come back yet (SS-7): it cannot be claimed until then. */
  SiteCustodyAway: 'site_custody_away',
  /** Too many saplings of the character growing at once (RES-GATHER PL-3). */
  PlantingLimit: 'planting_limit',
  /** A shop has none of the good left to sell (ECON-MARKET, MK-3). */
  ShopOutOfStock: 'shop_out_of_stock',
  /** A shop's till cannot pay what it would buy (ECON-MARKET, MK-1). */
  ShopNoCoin: 'shop_no_coin',
  /** A shop holds as much of the good as it takes (ECON-MARKET). */
  ShopFull: 'shop_full',
  /** The character is silenced in the chat (SOC-CHAT, OPS-MODERATION) for a while yet. */
  ChatMuted: 'chat_muted',
  /** Too many lines in a channel too fast (SOC-CHAT): its own pace, per character. */
  ChatRateLimited: 'chat_rate_limited',
  /** Nobody of that name is connected to hear a whisper (or would hear it, SOC-CHAT CH-5). */
  ChatTargetOffline: 'chat_target_offline',
  /** The party or company channel without a party or a company (SOC-CHAT). */
  ChatNotInChannel: 'chat_not_in_channel',
  /** As many characters ignored as the chat keeps (SOC-BLOCK). */
  ChatIgnoreFull: 'chat_ignore_full',
  /** The world channel asks a level first (SOC-CHAT CH-1). */
  ChatLevelTooLow: 'chat_level_too_low',
  /** A hall's candidate of another day, or one hired already (PROD-WORKER): its offer changed. */
  WorkerOfferChanged: 'worker_offer_changed',
  /** A hall has nobody available to hire now (WK-4): it refills by the day. */
  HallEmpty: 'hall_empty',
  /** A structure without posts for that trade (PROD-WORKER, AP-2). */
  PostUnfit: 'post_unfit',
  /** Every post of that structure is taken (AP-2: more are never assigned). */
  PostsFull: 'posts_full',
  /** A town will not work for whoever its power holds below neutral (PROD-WORKER F4, WK-4). */
  HallDistrusts: 'hall_distrusts',
  /** No quest of that id on this world (QUEST-CHAIN). */
  QuestUnknown: 'quest_unknown',
  /** The character lacks what the chain asks first (a level, a band, a chain before). */
  QuestRequirements: 'quest_requirements',
  /** Not within reach of that giver, or the chain is not taken or done there (QUEST technical 0). */
  QuestNotHere: 'quest_not_here',
  /** Nobody of that name to speak to within reach, or inside where nobody is spoken to (NPC-RUMOR). */
  FolkNotHere: 'folk_not_here',
  /** Not by that master at its work, or it does not teach that spell (MAGIC-GRIMOIRE MA-3). */
  TeacherNotHere: 'teacher_not_here',
  /** The chain's current step is not this giver's to do (QUEST-CHAIN). */
  QuestStepMismatch: 'quest_step_mismatch',
  /** The chain is done already: once per character (QU-1). */
  QuestDone: 'quest_done',
  /** The contract is no longer open, or its slots are gone (ECON-CONTRACT, QUEST-BOARD). */
  ContractClosed: 'contract_closed',
  /** The character took as many of the boards' tasks as a day or a week holds (QU-13). */
  ContractLimit: 'contract_limit',
  /** The character took a task of the same fact lately (QU-14: the memory window). */
  ContractRecent: 'contract_recent',
  /** A trade gives its tasks only to who stands neutral or better with its power (QU-16, TC-3). */
  BoardDistrusts: 'board_distrusts',
  /** An exclusive contract someone else has taken (KT-4). */
  ContractTaken: 'contract_taken',
  /** A contract taken, or with goods brought to it, cannot be cancelled (C3a). */
  ContractInProgress: 'contract_in_progress',
  /** No one takes a toll within reach: none near, alive and at rest (VB-1). */
  NoTollCollector: 'no_toll_collector',
  /** No wounded traveler waits within reach to be taken on its way (EN-3, VJ-1). */
  NoTraveler: 'no_traveler',
  /** The character or someone of its party already brings a traveler (EN-3: one at a time). */
  AlreadyEscorting: 'already_escorting',
  /** A treasure that opens only with its key, and the character carries none (IE-1, IL-4). */
  KeyRequired: 'key_required',
  /** A duel is challenged and accepted only with both in a sanctuary (B150, DU-3). */
  DuelOutsideSanctuary: 'duel_outside_sanctuary',
  /** Magic is cast only with a wand or a staff in the main hand (MU-1, WQ-3). */
  NoCaster: 'no_caster',
  /** A wall of ice where none may stand: a sanctuary, a town, another's lot, a road (ME-4). */
  WallForbidden: 'wall_forbidden',
  /** The Dissonance seals that technique (DEATH-PULSE §5, TO-5): unusable until it is cured. */
  TechniqueSealed: 'technique_sealed',
  /** A random treasure opens only with its guards down (WORLD-TREASURE QT-3). */
  TreasureGuarded: 'treasure_guarded',
  /** A room at a sanctuary's inn: its chest is the one thing safe, never forced (RV-14, AR-1). */
  RoomSafe: 'room_safe',
  /**
   * The place does not hold what the structure asks (BUILD technique 2, E4): a vein known under its
   * footprint (`vein_unknown`, never whether there is one), rich enough (`vein_poor`) and not spent
   * (`vein_spent`), or a field rich enough (`field_poor`).
   */
  SiteRequirement: 'site_requirement',
  /** An inn whose town fell rents no room: there is nobody to keep it (RV-13, IF-3). */
  InnFallen: 'inn_fallen',
  /** A summit material's recipe runs only where a master of its trade stands (CRAFT-MASTER R-83). */
  MasterRequired: 'master_required',
  /** As much of a site fell this damage window as one may ruin (SIEGE RX-1): wait for the next. */
  BreachCap: 'breach_cap',
  /** A hearth wounded this damage window takes no more (SIEGE RX-1): wait for the next. */
  HearthWounded: 'hearth_wounded',
  /** A loose house breaks in its Intense Pulse only with one of its people connected (LT-2). */
  OwnerAway: 'owner_away',
  /** A lot takes as many defences as its area allows (SIEGE SI-3, SG-9). */
  DefenseCap: 'defense_cap',
  /** A channel finds no foe in sight within its reach toward its aim (TECHNIQUE-BATCH-C). */
  NoTarget: 'no_target',
  /** A coalition of powers embargoes the character's faction here (NPC-KINGDOM-REACT, IK-4). */
  Embargoed: 'embargoed',
  /** No coalition stands against the character's faction: there is no peace to make (IM-4). */
  NoCoalition: 'no_coalition',
  /** Peace is made by a board of one of the coalition's powers, within its reach (IK-6). */
  PeaceNotHere: 'peace_not_here',
  /** A technique that spends a body finds none within its reach there (TECHNIQUE-BATCH-C §2.11). */
  NoBody: 'no_body',
  /** The console's admin password is wrong, or the account may not be raised (ADMIN-CONSOLE). */
  AdminRefused: 'admin_refused',
  /** Too many wrong admin passwords lately from this account or address: wait (ADMIN-CONSOLE). */
  AdminLocked: 'admin_locked',
  /** This server has no admin password set: nobody can be raised (ADMIN-CONSOLE). */
  AdminUnavailable: 'admin_unavailable',
  /** No end of a Depth's mouth within reach (DEPTH-ENTRANCE). */
  NoMouth: 'no_mouth',
  /** The character has no residue lying, nor one a band carried off (DEATH-PULSE PF-4, RN-2). */
  NoResidue: 'no_residue',
  /** The Wanderer asked heard nothing of the character's residue yet: nothing paid (RN-3, RN-4). */
  NothingHeard: 'nothing_heard',
  /**
   * A small site taken this damage window is not besieged again in it (SIEGE H2c), and what its
   * storages hold is nobody's to touch while a siege occupies it (CS-5).
   */
  SiteOccupied: 'site_occupied',
  /** Every place around a piece is taken by those striking it (SIEGE GZ-7): a crowd waits its turn. */
  BreachCrowded: 'breach_crowded',
  /**
   * A chain's step with its giver waits for the standing it lost meanwhile (QUEST, QL-1): its
   * `requires.band`, else above hostile with its power.
   */
  QuestStandingLow: 'quest_standing_low',
  /** What is made only by a fire, with no lit hearth nor camp within reach (BUILD, HOME rule 10). */
  NoFire: 'no_fire',
  /** Skipping a memory asked by an account that never ended a prologue (B165, PQ-6). */
  MemoryNotSkippable: 'memory_not_skippable',
  /** What a memory never lets go of while it lasts: its borrowed blade (B165, NM-6). */
  InMemory: 'in_memory',
  /** A blow, charge, guard or technique asked with the weapon put away (B167, SH-3). */
  WeaponSheathed: 'weapon_sheathed',
  /** An object worn below a quarter of its original maximum: it mends no more (ITEM-WEAR WR-5). */
  CannotRepair: 'cannot_repair',
  /** An object whose recipe gives nothing back, or that none makes (ITEM-WEAR WR-14, WR-15). */
  CannotSalvage: 'cannot_salvage',
  /** What a gathering spilled, taken by another while still its gatherer's (RES-GATHER RG-18). */
  DropClaimed: 'drop_claimed',
  /** A roofed piece used from outside: only from its floor or its threshold (WORLD-INTERIOR VW-10 a). */
  NotInside: 'not_inside',
  /** Loading a ship's hold, or buying one, away from a dock (MOVE-SHIP T7, NA-7). */
  NotAtDock: 'not_at_dock',
  /** A ship's action asked by one not aboard it (MOVE-SHIP T4). */
  NotAboard: 'not_aboard',
  /** Boarding a ship whose seats are all taken (MOVE-SHIP T4). */
  ShipCrowded: 'ship_crowded',
  /** Going ashore with no walkable ground within reach (MOVE-SHIP T4, NA-11: nobody swims yet). */
  NoLanding: 'no_landing',
  /** A world reset to a scenario written for another world layout than the server runs (B131). */
  ScenarioOtherWorld: 'scenario_other_world',
  /** A use of someone else's good its owner never granted (CORE-PERMISSION, T8). */
  AccessDenied: 'access_denied',
  /** A Depth's lift ridden from its head by someone who never opened it from its foot (KE-6). */
  LiftClosed: 'lift_closed',
  /** A passage by boat not opened yet: its quest not ended (OH-4, BJ-5). */
  PassageClosed: 'passage_closed',
  /** A pack too heavy for a passage by boat: the person rides, never the cargo (R-78, BJ-5). */
  PassageTooLoaded: 'passage_too_loaded',
} as const;

export type ErrorCode = (typeof ErrorCode)[keyof typeof ErrorCode];

export const ERROR_CODES = Object.values(ErrorCode) as readonly ErrorCode[];

export const ErrorCodeSchema = z.enum(ERROR_CODES as [ErrorCode, ...ErrorCode[]]);

/** Body of every non-2xx HTTP response. */
export const ApiErrorBodySchema = z.object({
  error: z.object({
    code: ErrorCodeSchema,
    message: z.string(),
    requestId: z.string(),
    details: z.unknown().optional(),
  }),
});
export type ApiErrorBody = z.infer<typeof ApiErrorBodySchema>;
